/**
 * Chest loot window (DJ 2026-10-23) — registry id `chestLoot`, opened by ChestPresenter when the
 * server answers `openChest` (chestLootView.ts). A tome page like the quest rewards: the chest's
 * name, each item with its icon, rarity and the shared item tooltip, then Take all. The server
 * grants on Take all (`takeChest`); a full bag or a step too far shows here and the chest stays.
 */
import React, { useSyncExternalStore } from 'react';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { ItemIcon } from '../../components/ItemIcon';
import { Tooltip, createItemTooltip } from '../../components/Tooltip';
import { rarityColor } from '../../components/itemkit/shared';
import { useItemTooltipViewer } from '../../hooks/useItemTooltipViewer';
import { TomeButton, TomeChapter, TomeChip } from '../../primitives/tome';
import { useHudTomeVibe } from '../../HUD/useHudTomeVibe';
import { useCurrentZoneId } from '../../hooks/useCurrentZoneId';
import {
  closeChestLoot, getChestLootView, subscribeChestLootView, takeChestLoot, type ChestLootStatus,
} from '../../../systems/network/chests/chestLootView';
import './ChestLootWindow.css';

/** The line under the list for each status; `contents` needs none. */
const STATUS_COPY: Partial<Record<ChestLootStatus, string>> = {
  taking: 'Taking…',
  taken: 'Taken — it is in your bag.',
  bag_full: 'Your bag is full. Make room and take it again.',
  too_far: 'Step closer to the chest to take it.',
  gone: 'The chest is empty.',
  already: 'You already took what was in here.',
};

const LootRow: React.FC<{ itemId: string; quantity: number }> = ({ itemId, quantity }) => {
  const viewer = useItemTooltipViewer();
  const item = ITEM_DATABASE[itemId];
  const name = item?.name ?? itemId;
  const row = (
    <span className="chest-loot-row" data-loot-item-id={itemId} tabIndex={item ? 0 : undefined}>
      {item && <span aria-hidden="true" className="chest-loot-icon"><ItemIcon itemId={itemId} size={40} /></span>}
      <span className="chest-loot-text">
        <span className="chest-loot-name" style={{ color: rarityColor(item?.rarity) }}>{name}{quantity > 1 && <strong> ×{quantity}</strong>}</span>
        {item?.rarity && <span className="chest-loot-rarity">{item.rarity}</span>}
      </span>
    </span>
  );
  return item ? <Tooltip inline data={createItemTooltip({ id: itemId, name }, viewer)}>{row}</Tooltip> : row;
};

export const ChestLootContent: React.FC = () => {
  const view = useSyncExternalStore(subscribeChestLootView, getChestLootView, getChestLootView);
  const tome = useHudTomeVibe(useCurrentZoneId());
  if (!view) return <div className={`chest-loot ${tome.className}`} style={tome.style}><p className="chest-loot-status">Nothing to loot.</p></div>;

  const canTake = view.items.length > 0 && (view.status === 'contents' || view.status === 'bag_full' || view.status === 'too_far');
  const copy = STATUS_COPY[view.status];
  return (
    <div className={`chest-loot ${tome.className}`} style={tome.style} data-testid="chest-loot" data-status={view.status}>
      <TomeChapter title={view.name} count={view.items.length || undefined}>
        {view.items.length > 0
          ? <ul className="chest-loot-list">{view.items.map((it, i) => <li key={`${it.itemId}-${i}`}><LootRow {...it} /></li>)}</ul>
          : null}
      </TomeChapter>
      {copy && <p className="chest-loot-status" role="status">
        {view.status === 'taken' ? <TomeChip tone="completed">{copy}</TomeChip> : copy}
      </p>}
      <div className="chest-loot-actions">
        {canTake && <TomeButton tone="primary" onClick={() => takeChestLoot()}>Take all</TomeButton>}
        <TomeButton tone="quiet" onClick={closeChestLoot}>Close</TomeButton>
      </div>
    </div>
  );
};
